import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Mail } from "lucide-react";
import Reveal from "@/components/motion/Reveal";
import MaskedReveal from "@/components/motion/MaskedReveal";
import HexMark from "@/components/motion/HexMark";
import Seo from "@/components/Seo";

// Reusable heading + copy blocks — keeps the legal page scannable
function Section({ id, numeral, title, children }) {
  return (
    <section id={id} data-testid={`privacy-section-${id}`} className="scroll-mt-28 mb-14">
      <div className="flex items-baseline gap-3 mb-4">
        <span className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)]">
          {numeral}
        </span>
        <h2 className="font-editorial text-2xl md:text-3xl leading-tight tracking-[-0.02em]">
          {title}
        </h2>
      </div>
      <div className="prose-brand max-w-3xl">{children}</div>
    </section>
  );
}

const TOC = [
  ["information", "§01", "Information We Collect"],
  ["how-we-use", "§02", "How We Use Your Information"],
  ["sharing", "§03", "How We Share Information"],
  ["cookies", "§04", "Cookies & Tracking"],
  ["security", "§05", "Data Security"],
  ["retention", "§06", "Data Retention"],
  ["your-rights", "§07", "Your Rights & Choices"],
  ["children", "§08", "Children's Privacy"],
  ["international", "§09", "International Transfers"],
  ["updates", "§10", "Updates to This Policy"],
  ["contact", "§11", "Contact Us"],
];

export default function Privacy() {
  return (
    <div className="bg-[var(--bg)] text-[var(--text)]">
      <Seo
        title="Privacy Policy"
        description="How Magee Enterprises collects, uses, protects, and shares personal information. Our commitment to your privacy and data rights."
        path="/privacy"
        keywords="privacy policy, data protection, cookies, GDPR, CCPA, personal information"
      />

      {/* HERO */}
      <section className="relative pt-36 md:pt-44 pb-14 md:pb-20 border-b border-[var(--border)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-45 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[520px] h-[520px] -top-32 -right-32 opacity-35" />
        <HexMark size={220} opacity={0.055} className="absolute -left-10 top-24 rotate-[8deg] pointer-events-none hidden md:block" />
        <span className="corner-cross" style={{ top: 40, right: 40 }} />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 lg:col-span-8">
            <div className="flex items-center gap-4 mb-10 flex-wrap">
              <span className="w-10 h-px bg-[var(--text)]/40" />
              <span className="font-mono-brand text-[11px] uppercase tracking-[0.28em] text-[var(--text-muted)]">
                Legal · Privacy
              </span>
              <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-cyan-700 border border-cyan-500/30 rounded-full px-2.5 py-1 bg-cyan-500/5 inline-flex items-center gap-1.5">
                <ShieldCheck size={11} /> Effective · February 2026
              </span>
            </div>
            <MaskedReveal
              lines={["Privacy", "policy."]}
              className="font-editorial tracking-[-0.03em] leading-[0.94] text-[13vw] md:text-[8vw] lg:text-[7vw]"
              stagger={0.09}
            />
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-col justify-end">
            <Reveal delay={0.35}>
              <p className="text-[var(--text)]/75 text-lg leading-relaxed">
                Magee Enterprises (&ldquo;<span className="text-[var(--text)] font-medium">Magee</span>,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) respects your privacy. This policy explains what we collect, how we use it, and the choices you have.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-primary" data-testid="privacy-hero-cta">
                  Ask a question <ArrowRight size={14} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BODY: sticky ToC + content */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 hex-pattern-dense opacity-30 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-6 md:gap-10">
            {/* ToC */}
            <aside className="col-span-12 lg:col-span-3">
              <div className="lg:sticky lg:top-28">
                <div className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)] mb-4 hidden lg:block">
                  Contents
                </div>
                <nav data-testid="privacy-toc" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-1 gap-2">
                  {TOC.map(([id, ref, label]) => (
                    <a
                      key={id}
                      href={`#${id}`}
                      className="group flex items-center gap-3 rounded-lg px-3 py-2 border border-transparent hover:border-[var(--border)] hover:bg-white/60 transition-all"
                    >
                      <span className="font-mono-brand text-[10px] tracking-[0.22em] text-[var(--text-muted)] group-hover:text-[var(--accent)]">
                        {ref}
                      </span>
                      <span className="text-[13px] text-[var(--text)]/75 group-hover:text-[var(--text)] leading-tight">
                        {label}
                      </span>
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Content */}
            <div className="col-span-12 lg:col-span-9">
              <div className="prose-brand max-w-none text-[15px] leading-[1.75] text-[var(--text)]/85">
                <p className="text-lg leading-relaxed text-[var(--text)]/80">
                  This Privacy Policy explains how Magee Enterprises collects, uses, discloses, and safeguards information when you visit
                  <span className="text-[var(--text)] font-medium"> mageeenterprises.com</span>, engage our event, meeting, hospitality, or B2B consulting services, book a consultation, or otherwise interact with our team. By using our services or website, you agree to the terms of this policy.
                </p>
              </div>

              <div className="mt-14 space-y-2">
                <Section id="information" numeral="§01" title="Information We Collect">
                  <p className="text-[15px] leading-[1.75] text-[var(--text)]/85">We collect information in three ways:</p>
                  <ul className="mt-3 space-y-2 text-[15px] leading-[1.75] text-[var(--text)]/85">
                    <li><span className="text-[var(--text)] font-medium">Information you provide directly</span> — name, email, phone, company, role, event type, attendee count, budget, dates, event notes, and any details submitted through our proposal forms, TidyCal booking widget, chat, or email correspondence.</li>
                    <li><span className="text-[var(--text)] font-medium">Information collected automatically</span> — IP address, browser type, device information, referring URL, pages visited, session duration, and interaction analytics via cookies or similar technologies.</li>
                    <li><span className="text-[var(--text)] font-medium">Information from third parties</span> — booking data from TidyCal, delivery status from SendLayer (our transactional email provider), and, where applicable, information from vendors, venues, or airlines processed on your behalf during a program.</li>
                  </ul>
                </Section>

                <Section id="how-we-use" numeral="§02" title="How We Use Your Information">
                  <p className="text-[15px] leading-[1.75] text-[var(--text)]/85">We use information to:</p>
                  <ul className="mt-3 space-y-2 text-[15px] leading-[1.75] text-[var(--text)]/85">
                    <li>Respond to proposal requests, quotes, and consultation bookings.</li>
                    <li>Deliver contracted event, meeting, hospitality, travel, and B2B services.</li>
                    <li>Communicate with you about your program, invoices, timelines, and confirmations.</li>
                    <li>Improve our services, measure engagement, and diagnose technical issues.</li>
                    <li>Comply with legal, tax, accounting, and regulatory obligations.</li>
                    <li>Prevent fraud, secure our systems, and enforce our Terms &amp; Conditions.</li>
                  </ul>
                  <p className="mt-3 text-[15px] leading-[1.75] text-[var(--text)]/85">
                    We do <span className="text-[var(--text)] font-medium">not</span> sell your personal information. We do not use your data for third-party advertising.
                  </p>
                </Section>

                <Section id="sharing" numeral="§03" title="How We Share Information">
                  <p className="text-[15px] leading-[1.75] text-[var(--text)]/85">
                    We share information only with the parties needed to deliver services or where legally required:
                  </p>
                  <ul className="mt-3 space-y-2 text-[15px] leading-[1.75] text-[var(--text)]/85">
                    <li><span className="text-[var(--text)] font-medium">Vendors &amp; venues</span> — hotels, airlines, ground transport, A/V, catering, staffing, and other suppliers strictly for the delivery of your program.</li>
                    <li><span className="text-[var(--text)] font-medium">Service providers</span> — TidyCal (scheduling), SendLayer (email delivery), MongoDB Atlas (data storage), analytics, and payment processors, each bound by their own privacy commitments.</li>
                    <li><span className="text-[var(--text)] font-medium">Legal &amp; regulatory bodies</span> — when required by law, subpoena, court order, or to protect our legal rights.</li>
                    <li><span className="text-[var(--text)] font-medium">Business transfers</span> — in the event of a merger, acquisition, or asset sale, subject to the same confidentiality standards.</li>
                  </ul>
                </Section>

                <Section id="cookies" numeral="§04" title="Cookies & Tracking">
                  <p className="text-[15px] leading-[1.75] text-[var(--text)]/85">
                    We use a small number of essential cookies to remember session state (for example, whether you have dismissed a booking prompt) and to measure site performance in aggregate. You can disable cookies through your browser settings; some features (such as the proposal wizard) may not function fully without them.
                  </p>
                </Section>

                <Section id="security" numeral="§05" title="Data Security">
                  <p className="text-[15px] leading-[1.75] text-[var(--text)]/85">
                    We implement reasonable administrative, technical, and physical safeguards, including TLS encryption in transit, access controls, and secured cloud storage. No system is impenetrable, however, and we cannot guarantee absolute security. If we ever become aware of a data breach that materially affects you, we will notify you and the relevant authorities in accordance with applicable law.
                  </p>
                </Section>

                <Section id="retention" numeral="§06" title="Data Retention">
                  <p className="text-[15px] leading-[1.75] text-[var(--text)]/85">
                    We retain personal information only for as long as needed to fulfill the purposes described in this policy, comply with our legal obligations (typically up to seven years for financial and tax records), resolve disputes, and enforce agreements. When information is no longer needed, we securely delete or de-identify it.
                  </p>
                </Section>

                <Section id="your-rights" numeral="§07" title="Your Rights & Choices">
                  <p className="text-[15px] leading-[1.75] text-[var(--text)]/85">
                    Depending on your jurisdiction (including under the GDPR, UK GDPR, and CCPA/CPRA), you may have the right to:
                  </p>
                  <ul className="mt-3 space-y-2 text-[15px] leading-[1.75] text-[var(--text)]/85">
                    <li>Access the personal information we hold about you.</li>
                    <li>Correct inaccurate or incomplete information.</li>
                    <li>Request deletion of your personal information, subject to legal exemptions.</li>
                    <li>Object to or restrict certain processing activities.</li>
                    <li>Withdraw consent where processing is based on consent.</li>
                    <li>Lodge a complaint with a supervisory authority.</li>
                  </ul>
                  <p className="mt-3 text-[15px] leading-[1.75] text-[var(--text)]/85">
                    To exercise any of these rights, email us at <a href="mailto:dion@mageeenterprises.com" className="text-[var(--accent)] underline underline-offset-4 hover:no-underline">dion@mageeenterprises.com</a>. We will respond within the timeframe required by law (typically 30 days).
                  </p>
                </Section>

                <Section id="children" numeral="§08" title="Children's Privacy">
                  <p className="text-[15px] leading-[1.75] text-[var(--text)]/85">
                    Our services are intended for business and professional use by adults. We do not knowingly collect personal information from children under the age of 16. If you believe a child has provided us with information, please contact us and we will delete it promptly.
                  </p>
                </Section>

                <Section id="international" numeral="§09" title="International Transfers">
                  <p className="text-[15px] leading-[1.75] text-[var(--text)]/85">
                    Magee Enterprises is headquartered in the United States. If you access our services from outside the U.S., your information may be transferred to, stored, and processed in the U.S. or other jurisdictions where we or our service providers operate. We rely on Standard Contractual Clauses or equivalent safeguards where required by law.
                  </p>
                </Section>

                <Section id="updates" numeral="§10" title="Updates to This Policy">
                  <p className="text-[15px] leading-[1.75] text-[var(--text)]/85">
                    We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. The &ldquo;Effective&rdquo; date at the top of the page will be revised accordingly. Material changes will be announced on the website or via direct notice where appropriate.
                  </p>
                </Section>

                <Section id="contact" numeral="§11" title="Contact Us">
                  <div className="rounded-2xl border border-[var(--border)] bg-white p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-sky-600 text-white flex items-center justify-center shrink-0 shadow-lg">
                      <Mail size={18} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-editorial text-lg md:text-xl leading-tight">Magee Enterprises · Privacy Team</div>
                      <div className="mt-2 text-[14.5px] text-[var(--text-muted)] leading-relaxed">
                        <a href="mailto:dion@mageeenterprises.com" className="text-[var(--accent)] underline underline-offset-4 hover:no-underline">dion@mageeenterprises.com</a>
                      </div>
                    </div>
                  </div>
                </Section>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
